'use client'

import { Suspense, useState, useEffect, useCallback, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  PackageSearch,
  Plus,
  Check,
  Layers,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { api, ApiError } from '@/lib/api'
import { API } from '@/config/env'
import type {
  CatalogSearchResult,
  CatalogBrand,
  Category,
  Condition,
  ApiSuccessResponse,
  Pagination,
  BulkAddData,
} from '@/shared/types'
import { Button } from '@/components/ui/button'

const CATEGORIES: { value: Category | ''; label: string }[] = [
  { value: '',              label: 'All categories' },
  { value: 'cars',         label: 'Cars' },
  { value: 'trucks',       label: 'Trucks' },
  { value: 'motorcycles',  label: 'Motorcycles' },
  { value: 'aircraft',     label: 'Aircraft' },
  { value: 'ships',        label: 'Ships' },
  { value: 'other',        label: 'Other' },
]

const MAX_BULK = 50

function normalizeImageUrl(url: string | null | undefined) {
  if (!url) return null;
  if (url.startsWith('http://')) return url.replace('http://', 'https://');
  return url;
}

// -----------------------------------------------------------------------------
// RESULT CARD
// -----------------------------------------------------------------------------

function CatalogResultCard({
  result,
  selectMode,
  isSelected,
  onToggle,
}: {
  result: CatalogSearchResult
  selectMode: boolean
  isSelected: boolean
  onToggle: () => void
}) {
  const inner = (
    <div
      className={`group flex items-center gap-4 rounded-xl border bg-card p-4 transition-all ${
        selectMode
          ? isSelected
            ? 'border-primary bg-primary/5 shadow-sm'
            : 'border-border hover:border-muted-foreground cursor-pointer'
          : 'border-border hover:-translate-y-0.5 hover:shadow-md'
      }`}
    >
      {/* Checkbox */}
      {selectMode && (
        <div
          className={`shrink-0 flex size-5 items-center justify-center rounded border-2 transition-colors ${
            isSelected
              ? 'border-primary bg-primary'
              : 'border-muted-foreground bg-background'
          }`}
        >
          {isSelected && <Check className="size-3 text-primary-foreground" strokeWidth={3} />}
        </div>
      )}

      {/* Image */}
      <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-muted">
        {normalizeImageUrl(result.imageUrl) ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={normalizeImageUrl(result.imageUrl) ?? ''}
            alt={result.name}
            className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <PackageSearch className="size-8 text-muted-foreground/40" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-foreground">{result.name}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {result.brand.name}
          {result.scale ? ` · ${result.scale}` : ''}
          {result.year ? ` · ${result.year}` : ''}
          {result.category ? ` · ${result.category}` : ''}
        </p>
        {result.series && (
          <p className="mt-0.5 text-xs text-muted-foreground">{result.series.name}</p>
        )}
      </div>

      {/* CTA only in browse mode */}
      {!selectMode && (
        <div className="shrink-0">
          <span className="text-xs font-medium text-primary group-hover:underline">View</span>
        </div>
      )}
    </div>
  )

  if (selectMode) {
    return (
      <button onClick={onToggle} className="w-full text-left">
        {inner}
      </button>
    )
  }

  return <Link href={`/catalog/${result.id}`}>{inner}</Link>
}

function BulkAddBar({
  items,
  condition,
  onConditionChange,
  onRemove,
  onSubmit,
  onCancel,
  submitting,
}: {
  items: CatalogSearchResult[]
  condition: Condition
  onConditionChange: (c: Condition) => void
  onRemove: (id: string) => void
  onSubmit: () => void
  onCancel: () => void
  submitting: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const count = items.length

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 shadow-lg backdrop-blur-sm">
      {/* Expandable selected-items panel */}
      {expanded && (
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="max-h-52 overflow-y-auto py-2">
            {items.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No items selected yet - tap a result to add it.
              </p>
            ) : (
              <ul className="space-y-1">
                {items.map(item => (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-muted"
                  >
                    {normalizeImageUrl(item.imageUrl) ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={normalizeImageUrl(item.imageUrl) ?? ''}
                        alt={item.name}
                        className="size-9 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
                        <PackageSearch className="size-4 text-muted-foreground/40" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {item.brand.name}
                        {item.scale ? ` · ${item.scale}` : ''}
                        {item.year ? ` · ${item.year}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => onRemove(item.id)}
                      disabled={submitting}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                      title="Remove"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-border" />
        </div>
      )}

      {/* Main bar */}
      <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3 sm:px-6">
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-primary"
        >
          <ChevronUp
            className={`size-4 transition-transform duration-200 ${expanded ? '' : 'rotate-180'}`}
          />
          {count} item{count !== 1 ? 's' : ''} selected
        </button>
        {count >= MAX_BULK && (
          <span className="text-xs text-destructive">(max {MAX_BULK})</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <select
            value={condition}
            onChange={e => onConditionChange(e.target.value as Condition)}
            disabled={submitting}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          >
            <option value="mint">Mint</option>
            <option value="excellent">Excellent</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
            <option value="poor">Poor</option>
          </select>

          <Button
            onClick={onSubmit}
            disabled={count === 0 || submitting}
            size="sm"
          >
            {submitting ? 'Adding...' : `Add ${count > 0 ? count : ''} to Collection`}
          </Button>

          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            title="Cancel selection"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// SEARCH CONTENT
// -----------------------------------------------------------------------------

function CatalogSearchContent() {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [query, setQuery]       = useState(searchParams.get('query') ?? '')
  const [brandFilter, setBrand] = useState(searchParams.get('brand') ?? '')
  const [category, setCategory] = useState<Category | ''>(
    (searchParams.get('category') as Category) ?? ''
  )
  const [page, setPage]         = useState(Number(searchParams.get('page') ?? 1))

  const [brands, setBrands]         = useState<CatalogBrand[]>([])
  const [results, setResults]       = useState<CatalogSearchResult[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [searching, setSearching]   = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // -- Bulk select state -------------------------------------------------------
  const [selectMode, setSelectMode]       = useState(false)
  const [selected, setSelected]           = useState<Map<string, CatalogSearchResult>>(new Map())
  const [bulkCondition, setBulkCondition] = useState<Condition>('mint')
  const [submitting, setSubmitting]       = useState(false)

  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestQueryRef = useRef(query)

  // Load brands once on mount
  useEffect(() => {
    api
      .get<ApiSuccessResponse<CatalogBrand[]>>(API.CATALOG_BRANDS)
      .then(r => setBrands(r.data))
      .catch(() => {/* non-critical */})
  }, [])

  const doSearch = useCallback(async (q: string, b: string, cat: Category | '', p: number) => {
    if (!q.trim()) {
      setResults([])
      setPagination(null)
      return
    }
    setSearching(true)
    try {
      const params: Record<string, string | number | undefined> = {
        query: q.trim(),
        page: p,
        limit: 20,
      }
      if (b) params.brand = b
      if (cat) params.category = cat

      const res = await api.get<ApiSuccessResponse<CatalogSearchResult[]>>(
        API.CATALOG_SEARCH,
        params
      )
      setResults(res.data)
      setPagination(res.pagination ?? null)
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message)
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const applySearch = useCallback((q: string, b: string, cat: Category | '', p: number) => {
    latestQueryRef.current = q
    const params = new URLSearchParams()
    if (q.trim()) params.set('query', q.trim())
    if (b) params.set('brand', b)
    if (cat) params.set('category', cat)
    if (p > 1) params.set('page', String(p))
    router.replace(`/catalog/search?${params.toString()}`, { scroll: false })
    doSearch(q, b, cat, p)
  }, [router, doSearch])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => applySearch(query, brandFilter, category, 1), 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, brandFilter, category])

  useEffect(() => {
    if (latestQueryRef.current.trim()) {
      doSearch(latestQueryRef.current, brandFilter, category, page)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handlePageChange = (next: number) => {
    setPage(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // -- Bulk select handlers ----------------------------------------------------

  const enterSelectMode = () => {
    setSelectMode(true)
    setSelected(new Map())
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelected(new Map())
  }

  const toggleItem = (result: CatalogSearchResult) => {
    setSelected(prev => {
      const next = new Map(prev)
      if (next.has(result.id)) {
        next.delete(result.id)
      } else {
        if (next.size >= MAX_BULK) {
          toast.error(`Maximum ${MAX_BULK} items can be added at once`)
          return prev
        }
        next.set(result.id, result)
      }
      return next
    })
  }

  const removeItem = (id: string) => {
    setSelected(prev => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  const handleBulkAdd = async () => {
    if (selected.size === 0) return
    setSubmitting(true)
    try {
      const items = Array.from(selected.values()).map(r => ({
        itemType: 'catalog' as const,
        catalogProductId: r.id,
        condition: bulkCondition,
      }))

      const result = await api.post<ApiSuccessResponse<BulkAddData>>(
        API.ITEMS_BULK,
        { items }
      )
      const { addedCount, skippedCount } = result.data

      let msg = `${addedCount} item${addedCount !== 1 ? 's' : ''} added to your collection`
      if (skippedCount > 0) msg += ` · ${skippedCount} skipped (already in collection)`
      toast.success(msg)

      exitSelectMode()
      router.push('/me')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to add items')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // Extra bottom padding when bulk bar is visible so content isn't hidden behind it
    <div className={`min-h-screen bg-background ${selectMode ? 'pb-24' : ''}`}>
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Catalog</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Browse verified diecast products
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!selectMode ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={enterSelectMode}
                  title="Select multiple items to bulk-add to your collection"
                >
                  <Layers className="size-4" />
                  Select Multiple
                </Button>
                <Link href="/catalog/submit">
                  <Button size="sm" variant="outline">
                    <Plus className="size-4" />
                    Submit Product
                  </Button>
                </Link>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Tap items to select them
              </p>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-4 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setPage(1) }}
              placeholder="Search by name, manufacturer, product code..."
              autoFocus
              className="w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${
              showFilters || brandFilter || category
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border bg-background text-muted-foreground hover:text-foreground'
            }`}
          >
            <Filter className="size-4" />
            Filters
            {(brandFilter || category) && (
              <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                {[brandFilter, category].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Brand</label>
              <select
                value={brandFilter}
                onChange={e => { setBrand(e.target.value); setPage(1) }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All brands</option>
                {brands.map(b => (
                  <option key={b.id} value={b.slug}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Category</label>
              <select
                value={category}
                onChange={e => { setCategory(e.target.value as Category | ''); setPage(1) }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {(brandFilter || category) && (
              <button
                onClick={() => { setBrand(''); setCategory(''); setPage(1) }}
                className="col-span-full text-left text-xs text-muted-foreground underline hover:text-foreground"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Results */}
        {searching && (
          <div className="flex items-center justify-center py-16">
            <div className="skeleton-brand h-3 w-28 rounded-full" />
          </div>
        )}

        {!searching && !query.trim() && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-muted">
              <Search className="size-8 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-base font-semibold text-foreground">Search the catalog</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              Find verified diecast products by name, car manufacturer, or product code.
            </p>
          </div>
        )}

        {!searching && query.trim() && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-muted">
              <PackageSearch className="size-8 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-base font-semibold text-foreground">No results</h3>
            <p className="mb-4 max-w-sm text-sm text-muted-foreground">
              No products found for &quot;{query}&quot;. Try different terms or submit a new product.
            </p>
            <Link href="/catalog/submit">
              <Button size="sm">Submit Product</Button>
            </Link>
          </div>
        )}

        {!searching && results.length > 0 && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {pagination
                  ? `${pagination.total.toLocaleString()} result${pagination.total !== 1 ? 's' : ''}`
                  : `${results.length} result${results.length !== 1 ? 's' : ''}`}
              </p>
              {selectMode && selected.size > 0 && (
                <p className="text-sm font-medium text-primary">
                  {selected.size} selected
                </p>
              )}
            </div>

            <div className="space-y-3">
              {results.map(r => (
                <CatalogResultCard
                  key={r.id}
                  result={r}
                  selectMode={selectMode}
                  isSelected={selected.has(r.id)}
                  onToggle={() => toggleItem(r)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft className="size-4" />
                  Prev
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= pagination.pages}
                  className="flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk add bar appears when in select mode */}
      {selectMode && (
        <BulkAddBar
          items={Array.from(selected.values())}
          condition={bulkCondition}
          onConditionChange={setBulkCondition}
          onRemove={removeItem}
          onSubmit={handleBulkAdd}
          onCancel={exitSelectMode}
          submitting={submitting}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// PAGE EXPORT
// -----------------------------------------------------------------------------

export default function CatalogSearchPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <div className="skeleton-brand h-3 w-28 rounded-full" />
        </div>
      }
    >
      <CatalogSearchContent />
    </Suspense>
  )
}



